import { useQuery } from '@apollo/client/react';
import { Box, Button, Center, Heading, Spinner, VStack } from '@chakra-ui/react';
import { CardCarousel, CardCarouselProps } from '@src/components/sco/CardCarousel';
import { BottomControlTest } from '@src/components/sco/CardCarousel/Controls/BottomControlTest';
import { TopControlMobile } from '@src/components/sco/CardCarousel/Controls/TopControlMobile';
import { UserMatchCard } from '@src/components/sco/Matching/UserMatchCard';
import { WorkspaceUserMatchCard } from '@src/components/sco/Matching/WorkspaceUserMatchCard';
import { UserAccessGate } from '@src/components/sco/UserAccessGate';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ScoUserMatch } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import React, { useCallback, useEffect, useState } from 'react';
export const UserMatchesCardList = (props: {
  hideTitle?: boolean;
  emptyMessage?: any;
  useCarousel?: boolean;
  useWorkspaceUserMatchCard?: boolean;
  showLoadMore?: boolean;
  carouselProps?: Partial<CardCarouselProps>;
  variables?: {
    order?: string[][];
    page?: number;
    pageSize?: number;
    searchingUserHasSaved?: boolean;
    searchingUserId?: string;
    targetUserHasSaved?: boolean;
    targetUserId?: string;
    userSearchId?: string;
  };
}) => {
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const query = useQuery(gql`
      query (
        $order: [[String]]
        $page: Int
        $pageSize: Int
        $userSearchId: String
        $searchingUserHasSaved: Boolean
        $searchingUserId: String
        $targetUserHasSaved: Boolean
        $targetUserId: String
      ) {
        scoUserMatches(
          order: $order
          page: $page
          pageSize: $pageSize
          userSearchId: $userSearchId
          searchingUserHasSaved: $searchingUserHasSaved
          searchingUserId: $searchingUserId
          targetUserHasSaved: $targetUserHasSaved
          targetUserId: $targetUserId
        ) {
          items {
            id
            userMatchId
          }
          pagination {
            hasNextPage
          }
        }
      }
    `, {
    fetchPolicy: 'network-only',
    variables: {
      ...(props.variables || {}),
      page: props.variables?.page || 1
    }
  });
  const showLoadMore = props.showLoadMore !== false && !props.useCarousel;
  const [page, setPage] = useState(props.variables?.page || 1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [extraMatches, setExtraMatches] = useState<ScoUserMatch[]>([]);
  const [hasMore, setHasMore] = useState(Boolean(query.data?.scoUserMatches?.pagination?.hasNextPage));
  useEffect(() => {
    setExtraMatches([]);
    setPage(props.variables?.page || 1);
  }, [props.variables?.userSearchId, props.variables?.searchingUserHasSaved, props.variables?.page]);
  useEffect(() => {
    if (page === 1) {
      setHasMore(Boolean(query.data?.scoUserMatches?.pagination?.hasNextPage));
    }
  }, [page, query.data?.scoUserMatches?.pagination?.hasNextPage]);
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) {
      return;
    }
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const result = await query.fetchMore({
        variables: {
          page: nextPage
        }
      });
      const incoming = result.data?.scoUserMatches;
      const nextItems = (incoming?.items ?? []).filter(Boolean) as ScoUserMatch[];
      setExtraMatches(current => {
        const seen = new Set(current.map(item => item.userMatchId || item.id));
        return [...current, ...nextItems.filter(item => !seen.has(item.userMatchId || item.id))];
      });
      setHasMore(Boolean(incoming?.pagination?.hasNextPage));
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, page, query]);
  const firstPageMatches = (query.data?.scoUserMatches?.items ?? []).filter(Boolean) as ScoUserMatch[];
  const userMatches = [...firstPageMatches, ...extraMatches];
  if (!query.loading && userMatches?.length == 0 && props.emptyMessage) {
    return <Box width={'full'}>{props.emptyMessage}</Box>;
  }
  return <UserAccessGate ctaVariant={'boxed-card'} require={{
    isRegistered: true
  }}>
      <VStack height={'full'} overflowY={props.useCarousel ? 'visible' : 'auto'} width={'full'}>
        {!props.hideTitle && <Center>
            <Heading background={'linear-gradient(90deg, #d97258 0%, #9858a3 100%)'} backgroundClip={'text'} color={'transparent'} paddingY={'1em'} size={['2xl', '4xl']} textAlign={'center'}>
              Saved Matches
            </Heading>
          </Center>}

        {query.loading && !query.data && <Center padding={'1em'}>
            <Spinner />
          </Center>}
        {userMatches?.length > 0 ? props.useCarousel ? <CardCarousel scrollSnapAlign={'center'} spacing={0} visibleCards={[1]} controls={{
        top: getBreakpointValue([TopControlMobile, 'false', 'false']),
        bottom: BottomControlTest
      }} {...props.carouselProps}>
              {userMatches.map((userMatch: ScoUserMatch) => props.useWorkspaceUserMatchCard ? <WorkspaceUserMatchCard key={userMatch.id} showUserSearchBanner={true} trackView={true} userMatchId={userMatch.userMatchId} /> : <UserMatchCard key={userMatch.id} showUserSearchBanner={true} trackView={true} userMatchId={userMatch.userMatchId} />)}
            </CardCarousel> : <VStack gap={4} padding={2} width={'full'}>
              {userMatches.map((userMatch_0: ScoUserMatch) => props.useWorkspaceUserMatchCard ? <WorkspaceUserMatchCard key={userMatch_0.id} showUserSearchBanner={true} trackView={true} userMatchId={userMatch_0.userMatchId} /> : <UserMatchCard key={userMatch_0.id} showUserSearchBanner={true} trackView={true} userMatchId={userMatch_0.userMatchId} />)}
              {showLoadMore && hasMore ? <Button loading={loadingMore} size={'sm'} variant={'outline'} width={'full'} onClick={loadMore}>
                  Load more
                </Button> : null}
            </VStack> : <Center padding={'1em'} width={'full'}>
            <VStack gap={'4'} maxWidth={'container.xl'} width={'full'}>
              <Heading>No saved matches yet.</Heading>
              <Button asChild>
                <NextLink href={'/members/matching'}>Start a New Match</NextLink>
              </Button>
            </VStack>
          </Center>}
      </VStack>
    </UserAccessGate>;
};